(() => {
  const DAY_MS = 24 * 60 * 60 * 1000;
  const EVENT_VERSION = 'welcome_offer_v2.4';

  const publishEvent = (eventName, properties = {}) => {
    const payload = {
      event: eventName,
      welcome_offer_version: EVENT_VERSION,
      popup_variant: 'split_blue_minimal_v1',
      source: 'theme_popup',
      page_path: window.location.pathname,
      ...properties
    };
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(payload);
    document.dispatchEvent(new CustomEvent('ingenio:welcome-offer', { detail: payload }));
  };

  const recordAutoAppliedDiscount = () => {
    const url = new URL(window.location.href);
    if (url.searchParams.get('welcome_offer') !== 'applied') return;

    const deliveryChannel = url.searchParams.get('utm_medium') || 'unknown';
    const applicationMethod = deliveryChannel === 'whatsapp'
      ? 'whatsapp_auto_apply_link'
      : deliveryChannel === 'email'
        ? 'email_auto_apply_link'
        : 'auto_apply_link';

    publishEvent('welcome_code_applied', {
      application_method: applicationMethod,
      application_evidence: 'discount_redirect_completed',
      delivery_channel: deliveryChannel
    });
    try {
      const current = JSON.parse(window.localStorage.getItem('ingenio.welcome-offer.v2') || '{}');
      window.localStorage.setItem('ingenio.welcome-offer.v2', JSON.stringify({
        ...current,
        status: 'applied',
        appliedAt: new Date().toISOString(),
        version: 2
      }));
    } catch (error) {
      // Attribution still reaches the data layer when storage is unavailable.
    }

    url.searchParams.delete('welcome_offer');
    window.history.replaceState({}, document.title, `${url.pathname}${url.search}${url.hash}`);
  };

  recordAutoAppliedDiscount();

  class IngenioWelcomeOffer extends HTMLElement {
    connectedCallback() {
      if (this.dataset.initialized === 'true') return;
      this.dataset.initialized = 'true';

      this.dialog = this.querySelector('.ingenio-welcome-offer__dialog');
      this.form = this.querySelector('form');
      this.formPanel = this.querySelector('[data-welcome-form-panel]');
      this.successPanel = this.querySelector('[data-welcome-success]');
      this.email = this.querySelector('[data-welcome-email]');
      this.whatsapp = this.querySelector('[data-welcome-whatsapp]');
      this.country = this.querySelector('[data-welcome-country]');
      this.emailConsent = this.querySelector('[data-welcome-email-consent]');
      this.whatsappConsent = this.querySelector('[data-welcome-whatsapp-consent]');
      this.tags = this.querySelector('[data-welcome-tags]');
      this.serverError = this.querySelector('[data-welcome-server-error]');
      this.closeButtons = this.querySelectorAll('[data-welcome-close]');
      this.storageKey = this.dataset.storageKey || 'ingenio.welcome-offer.v2';
      this.dismissDays = Number(this.dataset.dismissDays || 15);
      this.delayMs = Number(this.dataset.delaySeconds || 8) * 1000;
      this.testMode = this.dataset.testMode === 'true' || new URLSearchParams(window.location.search).get('welcome_offer_test') === '1';
      this.previouslyFocused = null;

      this.onKeydown = this.handleKeydown.bind(this);
      this.onClose = this.dismiss.bind(this);
      this.onSubmit = this.handleSubmit.bind(this);
      this.onWhatsappChange = this.syncWhatsappField.bind(this);

      this.closeButtons.forEach((button) => button.addEventListener('click', this.onClose));
      this.form?.addEventListener('submit', this.onSubmit);
      this.whatsappConsent?.addEventListener('change', this.onWhatsappChange);
      this.syncWhatsappField();

      if (this.successPanel?.dataset.postedSuccessfully === 'true') {
        this.saveState({ status: 'submitted', submittedAt: new Date().toISOString() });
        this.open('success');
        this.track('welcome_registration_success');
        return;
      }

      if (this.serverError) {
        this.open('form');
        this.serverError.focus();
        this.track('welcome_registration_error', { error_source: 'shopify_customer_form' });
        return;
      }

      if (!this.shouldOpen()) return;
      this.openTimer = window.setTimeout(() => this.open('form'), this.testMode ? 150 : this.delayMs);
    }

    disconnectedCallback() {
      window.clearTimeout(this.openTimer);
      document.removeEventListener('keydown', this.onKeydown);
      this.closeButtons?.forEach((button) => button.removeEventListener('click', this.onClose));
      this.form?.removeEventListener('submit', this.onSubmit);
      this.whatsappConsent?.removeEventListener('change', this.onWhatsappChange);
    }

    readState() {
      try {
        return JSON.parse(window.localStorage.getItem(this.storageKey) || '{}');
      } catch (error) {
        return {};
      }
    }

    saveState(nextState) {
      try {
        const current = this.readState();
        window.localStorage.setItem(this.storageKey, JSON.stringify({ ...current, ...nextState, version: 2 }));
      } catch (error) {
        // The offer remains functional when storage is unavailable.
      }
    }

    shouldOpen() {
      if (this.testMode) return true;
      const state = this.readState();
      if (['submitted', 'applied', 'purchased'].includes(state.status) || state.submittedAt) return false;
      if (!state.dismissedAt) return true;
      const dismissedAt = Date.parse(state.dismissedAt);
      if (Number.isNaN(dismissedAt)) return true;
      return Date.now() - dismissedAt >= this.dismissDays * DAY_MS;
    }

    open(panel = 'form') {
      window.clearTimeout(this.openTimer);
      this.previouslyFocused = document.activeElement;
      this.formPanel?.classList.toggle('is-hidden', panel !== 'form');
      this.successPanel?.classList.toggle('is-hidden', panel !== 'success');
      this.hidden = false;
      document.body.classList.add('ingenio-welcome-offer-open');
      document.addEventListener('keydown', this.onKeydown);
      window.requestAnimationFrame(() => {
        this.classList.add('is-open');
        const target = panel === 'success' ? this.successPanel : this.email;
        target?.focus({ preventScroll: true });
      });
      if (panel === 'form') this.track('welcome_popup_view');
    }

    dismiss(event) {
      if (event) event.preventDefault();
      const isSuccess = !this.successPanel?.classList.contains('is-hidden');
      if (!isSuccess) {
        this.saveState({ status: 'dismissed', dismissedAt: new Date().toISOString() });
        this.track('welcome_popup_dismiss');
      }
      this.classList.remove('is-open');
      document.body.classList.remove('ingenio-welcome-offer-open');
      document.removeEventListener('keydown', this.onKeydown);
      window.setTimeout(() => {
        this.hidden = true;
        if (this.previouslyFocused instanceof HTMLElement) this.previouslyFocused.focus({ preventScroll: true });
      }, 180);
    }

    handleKeydown(event) {
      if (event.key === 'Escape') {
        this.dismiss(event);
        return;
      }
      if (event.key !== 'Tab') return;
      const focusable = Array.from(this.dialog.querySelectorAll('button:not([disabled]), input:not([disabled]), a[href], [tabindex]:not([tabindex="-1"])'))
        .filter((element) => element.offsetParent !== null);
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    syncWhatsappField() {
      if (!this.whatsapp || !this.whatsappConsent || !this.country) return;
      this.whatsapp.required = true;
      this.country.required = true;
      this.whatsappConsent.required = true;
      if (this.whatsappConsent.checked) this.whatsappConsent.setCustomValidity('');
    }

    getCallingCode(value) {
      const match = String(value || '').match(/\+(\d{1,4})/);
      return match ? `+${match[1]}` : '';
    }

    normalizeWhatsapp(value, countryValue) {
      const raw = String(value || '').trim();
      const digits = raw.replace(/\D/g, '');
      if (raw.startsWith('+') && digits.length >= 8 && digits.length <= 15) return `+${digits}`;

      const callingCode = this.getCallingCode(countryValue);
      if (!callingCode) return '';
      const callingDigits = callingCode.replace(/\D/g, '');
      if (digits.startsWith(callingDigits) && digits.length >= 8 && digits.length <= 15) return `+${digits}`;

      const nationalDigits = digits.replace(/^0+/, '');
      const combined = `${callingDigits}${nationalDigits}`;
      return combined.length >= 8 && combined.length <= 15 ? `+${combined}` : '';
    }

    handleSubmit(event) {
      let firstInvalidConsent = null;
      if (!this.emailConsent?.checked) {
        this.emailConsent.setCustomValidity('Debes aceptar las comunicaciones por correo electrónico para recibir el código.');
        firstInvalidConsent = this.emailConsent;
      } else {
        this.emailConsent.setCustomValidity('');
      }

      if (!this.whatsappConsent?.checked) {
        this.whatsappConsent?.setCustomValidity('Debes aceptar las promociones por WhatsApp para recibir el código.');
        firstInvalidConsent ||= this.whatsappConsent;
      } else {
        this.whatsappConsent.setCustomValidity('');
      }

      if (firstInvalidConsent) {
        event.preventDefault();
        firstInvalidConsent.reportValidity();
        return;
      }

      const callingCode = this.getCallingCode(this.country?.value);
      if (!callingCode) {
        event.preventDefault();
        this.country?.setCustomValidity('Busca un país o escribe un código como +57.');
        this.country?.reportValidity();
        return;
      }
      this.country.setCustomValidity('');

      const normalizedWhatsapp = this.normalizeWhatsapp(this.whatsapp?.value, this.country.value);
      if (!normalizedWhatsapp) {
        event.preventDefault();
        this.whatsapp?.setCustomValidity('Escribe un número de WhatsApp válido e incluye su país.');
        this.whatsapp?.reportValidity();
        return;
      }
      this.whatsapp.setCustomValidity('');
      this.whatsapp.value = normalizedWhatsapp;

      if (!this.form.checkValidity()) {
        event.preventDefault();
        this.form.reportValidity();
        return;
      }

      const consentTags = [
        'newsletter',
        'welcome-offer-request-v2',
        'welcome-offer-email-consent',
        'welcome-offer-whatsapp-consent'
      ];
      const whatsappDigits = this.whatsapp.value.replace(/\D/g, '');
      consentTags.push(`welcome-offer-wa-${whatsappDigits}`);
      this.tags.value = consentTags.join(', ');
      this.saveState({ status: 'pending', submittedAt: null, requestedAt: new Date().toISOString() });
      this.track('welcome_offer_submit', { email_opt_in: true, whatsapp_opt_in: true, country_code: callingCode });
      this.track('welcome_email_submit', { whatsapp_opt_in: true });
      this.track('welcome_whatsapp_opt_in');
    }

    track(eventName, properties = {}) {
      publishEvent(eventName, properties);
    }
  }

  if (!customElements.get('ingenio-welcome-offer')) {
    customElements.define('ingenio-welcome-offer', IngenioWelcomeOffer);
  }
})();
